import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { QUERY_KEYS } from '@/lib/queryKeys';
import { retryDelayByErrorKind, shouldRetryByErrorKind, throwApiErrorFromResponse } from '@/lib/errorFlow';

/**
 * Security settings type
 */
export interface SecuritySettings {
  autoLogoutEnabled: boolean;
  autoLogoutTimeout: number; // minutes
}

/**
 * Fetch security settings from API
 */
const fetchSecuritySettings = async (): Promise<SecuritySettings> => {
  const response = await api.get('/settings/security');
  
  if (!response.ok || !response.data?.success) {
    throwApiErrorFromResponse(response, 'Không thể tải cài đặt bảo mật', 'useSecuritySettings:/settings/security');
  }
  
  const data = response.data.data;
  return {
    autoLogoutEnabled: data.autoLogoutEnabled ?? true,
    autoLogoutTimeout: data.autoLogoutTimeout ?? 30
  };
};

/**
 * Custom hook to fetch security settings with React Query
 * 
 * Features:
 * - Cache: 5 minutes (staleTime) - settings don't change frequently
 * - Automatic refetch on window focus
 * - Shared cache across all components using this hook
 * - Automatic deduplication (multiple components = single API call)
 * - Fallback to localStorage if API fails (offline support)
 * 
 * Usage:
 * ```tsx
 * const { data: settings, isLoading } = useSecuritySettings();
 * ```
 * 
 * Invalidation (after mutations):
 * ```tsx
 * // After update settings:
 * queryClient.invalidateQueries({ queryKey: QUERY_KEYS.settings.security });
 * ```
 */
export const useSecuritySettings = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  
  return useQuery({
    queryKey: QUERY_KEYS.settings.security,
    queryFn: fetchSecuritySettings,
    enabled: !!token, // Only fetch when token exists
    staleTime: 5 * 60 * 1000, // 5 minutes - settings don't change frequently
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
    retry: shouldRetryByErrorKind,
    retryDelay: retryDelayByErrorKind,
    // Fallback to localStorage if API fails
    placeholderData: () => {
      if (typeof window === 'undefined') return undefined;
      const enabled = localStorage.getItem('autoLogoutEnabled');
      const timeout = localStorage.getItem('autoLogoutTimeout');
      return {
        autoLogoutEnabled: enabled !== 'false', // Default true if not explicitly 'false'
        autoLogoutTimeout: timeout ? parseInt(timeout, 10) : 30
      };
    },
    throwOnError: false, // Don't throw on error, use placeholderData instead
  });
};

